import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Dialog } from '@headlessui/react';
import {
  ShieldCheckIcon,
  PlusIcon,
  ArrowDownTrayIcon,
  TrashIcon,
  EyeIcon,
  ArrowLeftIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { riskAssessmentApi } from '../lib/api';
import { useTeamId } from '../hooks/useTeamId';
import FeatureGate from '../components/FeatureGate';
import PermissionGate from '../components/PermissionGate';
import ModuleConnections from '../components/help/ModuleConnections';
import clsx from 'clsx';

// The generator emits LOW / MEDIUM / HIGH (riskAssessment.routes.ts); MEDIUM had no badge
// and fell through to the LOW (green) style (2026-10-17 module review).
const RISK_LEVEL_BADGE: Record<string, string> = {
  LOW: 'bg-green-100 text-green-700',
  MEDIUM: 'bg-yellow-100 text-yellow-700',
  MODERATE: 'bg-yellow-100 text-yellow-700',
  HIGH: 'bg-red-100 text-red-700',
  CRITICAL: 'bg-red-200 text-red-800',
};

const STATUS_BADGE: Record<string, string> = {
  GENERATING: 'bg-yellow-100 text-yellow-700',
  DRAFT: 'bg-gray-100 text-gray-700',
  UNDER_REVIEW: 'bg-blue-100 text-blue-700',
  FINALIZED: 'bg-green-100 text-green-700',
  ERROR: 'bg-red-100 text-red-700',
};

// A failed generation is persisted as status DRAFT + errorMessage (the status enum has no
// failure value), so errorMessage is what marks a row as failed.
const isFailed = (a: any) => !!a?.errorMessage;
const displayStatus = (a: any) => (isFailed(a) ? 'ERROR' : a.status);

// ── sectionsJson renderer ─────────────────────────────────────────
// The backend returns structured JSON in `sectionsJson` (the page used to read a `content`
// field that never existed — 2026-10-17 module review). Known generator sections render
// first in a fixed order; anything else (e.g. the demo seed's { title, content, items }
// sections) renders after, titled by its own `title`.
const SECTION_TITLES: Record<string, string> = {
  executiveSummary: 'Executive Summary',
  hmdaSummary: 'HMDA Data Summary',
  denialRateAnalysis: 'Denial Rate Analysis',
  peerComparison: 'Peer Comparison',
  exceptionSummary: 'Exception Tracking Summary',
  recommendations: 'Recommendations',
};
const FIELD_LABELS: Record<string, string> = {
  validationsCount: 'HMDA validations',
  totalLoans: 'Total loans',
  cleanLoans: 'Clean loans',
  cleanPct: 'Clean loan rate (%)',
  analysesRun: 'Fair lending analyses run',
  highRiskFindings: 'High-risk findings',
  mediumRiskFindings: 'Medium-risk findings',
  topFindings: 'Top high-risk findings',
  yourDenialRate: 'Your denial rate',
  peerDenialRate: 'Peer denial rate',
  yourBlackWhiteRatio: 'Your Black/White denial ratio',
  peerBlackWhiteRatio: 'Peer Black/White denial ratio',
  yourHispanicWhiteRatio: 'Your Hispanic/White denial ratio',
  peerHispanicWhiteRatio: 'Peer Hispanic/White denial ratio',
  totalExceptions: 'Total exceptions',
  byType: 'By type',
  byStatus: 'By status',
};
const humanize = (key: string) =>
  FIELD_LABELS[key] ||
  key.replace(/_/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2').replace(/^./, (c) => c.toUpperCase());
const isPlainObject = (v: unknown): v is Record<string, unknown> =>
  !!v && typeof v === 'object' && !Array.isArray(v);

function orderedSectionKeys(sections: Record<string, unknown>): string[] {
  const known = Object.keys(SECTION_TITLES).filter((k) => sections[k] != null);
  return [...known, ...Object.keys(sections).filter((k) => !(k in SECTION_TITLES) && sections[k] != null)];
}

function sectionTitle(key: string, value: unknown): string {
  if (isPlainObject(value) && typeof value.title === 'string') return value.title;
  return SECTION_TITLES[key] || humanize(key);
}

function SectionValue({ value }: { value: unknown }) {
  if (value == null || value === '') return <p className="text-sm text-gray-400">None</p>;
  if (typeof value !== 'object') {
    return (
      <div className="space-y-2">
        {String(value).split(/\n{2,}/).map((para, i) => (
          <p key={i} className="text-sm text-gray-600 whitespace-pre-wrap">{para}</p>
        ))}
      </div>
    );
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return <p className="text-sm text-gray-400">None</p>;
    return (
      <ul className="list-disc pl-5 space-y-1">
        {value.map((item, i) => {
          if (!isPlainObject(item)) return <li key={i} className="text-sm text-gray-600">{String(item)}</li>;
          const { title, description, ...rest } = item;
          const extras = Object.entries(rest).filter(([, v]) => v != null && v !== '' && typeof v !== 'object');
          return (
            <li key={i} className="text-sm text-gray-600">
              {title != null && <span className="font-medium text-gray-900">{String(title)}</span>}
              {description != null && <span>{title != null ? ' — ' : ''}{String(description)}</span>}
              {extras.length > 0 && (
                <span className="text-gray-500">
                  {' '}({extras.map(([k, v]) => `${humanize(k)}: ${v}`).join('; ')})
                </span>
              )}
            </li>
          );
        })}
      </ul>
    );
  }
  const obj = value as Record<string, unknown>;
  // Demo-seed shape: { title, content, items, ...metrics } — content + items carry the text.
  if ('content' in obj || 'items' in obj) {
    return (
      <div className="space-y-2">
        {obj.content != null && <SectionValue value={obj.content} />}
        {obj.items != null && <SectionValue value={obj.items} />}
      </div>
    );
  }
  const entries = Object.entries(obj);
  if (entries.length === 0) return <p className="text-sm text-gray-400">None</p>;
  return (
    <dl className="grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2">
      {entries.map(([k, v]) => (
        <div key={k} className={clsx(typeof v === 'object' && v != null && 'sm:col-span-2')}>
          <dt className="text-xs font-medium text-gray-500">{humanize(k)}</dt>
          <dd className="mt-0.5 text-sm text-gray-900">
            {typeof v === 'object' && v != null ? <SectionValue value={v} /> : String(v ?? '—')}
          </dd>
        </div>
      ))}
    </dl>
  );
}

export default function RiskAssessmentPage() {
  const queryClient = useQueryClient();
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  // Form state
  const [formTitle, setFormTitle] = useState('');
  const [formPeriodStart, setFormPeriodStart] = useState('');
  const [formPeriodEnd, setFormPeriodEnd] = useState('');

  // Team
  const { teamId } = useTeamId();

  // List
  const { data: listData, isLoading } = useQuery({
    queryKey: ['risk-assessments', teamId],
    queryFn: () => riskAssessmentApi.list(teamId!, { limit: 50 }),
    enabled: !!teamId,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (data?.data?.assessments?.some((a: any) => a.status === 'GENERATING' && !isFailed(a))) return 5000;
      return false;
    },
  });
  const assessments = listData?.data?.assessments || [];

  // Detail — polls while the open assessment is still generating so a completion or a
  // recorded failure shows up without a manual refresh.
  const { data: detailData, isLoading: detailLoading } = useQuery({
    queryKey: ['risk-assessment-detail', teamId, selectedId],
    queryFn: () => riskAssessmentApi.get(teamId!, selectedId!),
    enabled: !!teamId && !!selectedId,
    refetchInterval: (query) => {
      const d = query.state.data?.data;
      return d?.status === 'GENERATING' && !isFailed(d) ? 5000 : false;
    },
  });
  const detail = detailData?.data;
  const detailSections: Record<string, unknown> | null = isPlainObject(detail?.sectionsJson) ? detail.sectionsJson : null;

  // Summary counts
  const totalCount = assessments.length;
  const generatingCount = assessments.filter((a: any) => a.status === 'GENERATING').length;
  const draftCount = assessments.filter((a: any) => a.status === 'DRAFT' && !isFailed(a)).length;
  const finalizedCount = assessments.filter((a: any) => a.status === 'FINALIZED').length;

  // Generate mutation
  const generateMutation = useMutation({
    mutationFn: () =>
      riskAssessmentApi.generate(teamId!, {
        title: formTitle,
        periodStart: formPeriodStart,
        periodEnd: formPeriodEnd,
      }),
    onSuccess: () => {
      toast.success('Risk assessment generation started');
      queryClient.invalidateQueries({ queryKey: ['risk-assessments'] });
      setShowGenerateModal(false);
      resetForm();
    },
    onError: (err: any) => toast.error(err.response?.data?.error || 'Failed to generate'),
  });

  // Update status mutation
  const updateStatusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      riskAssessmentApi.update(teamId!, id, { status }),
    onSuccess: () => {
      toast.success('Status updated');
      queryClient.invalidateQueries({ queryKey: ['risk-assessments'] });
      queryClient.invalidateQueries({ queryKey: ['risk-assessment-detail'] });
    },
    onError: (err: any) => toast.error(err.response?.data?.error || 'Failed to update status'),
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => riskAssessmentApi.delete(teamId!, id),
    onSuccess: () => {
      toast.success('Assessment deleted');
      queryClient.invalidateQueries({ queryKey: ['risk-assessments'] });
      setDeleteConfirm(null);
      if (selectedId === deleteConfirm) setSelectedId(null);
    },
    onError: () => toast.error('Failed to delete'),
  });

  // Download PDF
  const handleDownloadPdf = async (id: string, title: string) => {
    try {
      const res = await riskAssessmentApi.downloadPdf(teamId!, id);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${title.replace(/\s+/g, '-').toLowerCase()}-risk-assessment.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to download PDF');
    }
  };

  const resetForm = () => {
    setFormTitle('');
    setFormPeriodStart('');
    setFormPeriodEnd('');
  };

  const formatDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <FeatureGate feature="risk_assessment_generator" mode="lock">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Risk Assessments</h1>
            <p className="mt-1 text-sm text-gray-500">
              Generate and manage compliance risk assessments for your institution
            </p>
          </div>
          <PermissionGate teamId={teamId} permission="compliance.write">
            <button
              onClick={() => setShowGenerateModal(true)}
              disabled={!teamId}
              className="inline-flex items-center gap-1.5 rounded-lg bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-700 disabled:opacity-50"
            >
              <PlusIcon className="h-4 w-4" />
              Generate Assessment
            </button>
          </PermissionGate>
        </div>

        <ModuleConnections moduleKey="fair-lending-risk-assessments" />

        {/* Summary Cards */}
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <div className="rounded-lg border bg-white p-4">
            <p className="text-xs font-medium text-gray-500">Total Assessments</p>
            <p className="mt-1 text-2xl font-bold text-gray-900">{totalCount}</p>
          </div>
          <div className="rounded-lg border bg-white p-4">
            <p className="text-xs font-medium text-gray-500">Generating</p>
            <p className={clsx(
              'mt-1 text-2xl font-bold',
              // Only highlight in yellow when there's actually something
              // generating — zero of a transient state shouldn't draw the
              // eye as if it's a problem.
              generatingCount > 0 ? 'text-yellow-600' : 'text-gray-900',
            )}>{generatingCount}</p>
          </div>
          <div className="rounded-lg border bg-white p-4">
            <p className="text-xs font-medium text-gray-500">Draft</p>
            <p className="mt-1 text-2xl font-bold text-gray-600">{draftCount}</p>
          </div>
          <div className="rounded-lg border bg-white p-4">
            <p className="text-xs font-medium text-gray-500">Finalized</p>
            <p className="mt-1 text-2xl font-bold text-green-600">{finalizedCount}</p>
          </div>
        </div>

        {/* Detail View */}
        {selectedId && (
          <div className="rounded-lg border bg-white">
            <div className="border-b px-4 py-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setSelectedId(null)}
                    className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                  >
                    <ArrowLeftIcon className="h-5 w-5" />
                  </button>
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900">{detail?.title || 'Loading...'}</h3>
                    {detail && (
                      <p className="text-xs text-gray-400 mt-0.5">
                        {formatDate(detail.assessmentPeriodStart)} - {formatDate(detail.assessmentPeriodEnd)}
                      </p>
                    )}
                  </div>
                </div>
                {detail && (
                  <div className="flex items-center gap-2">
                    {detail.status === 'DRAFT' && !isFailed(detail) && (
                      <button
                        onClick={() => updateStatusMutation.mutate({ id: selectedId, status: 'UNDER_REVIEW' })}
                        className="rounded-lg border border-blue-300 bg-blue-50 px-3 py-1.5 text-xs font-medium text-blue-700 hover:bg-blue-100"
                      >
                        Mark as Under Review
                      </button>
                    )}
                    {detail.status === 'UNDER_REVIEW' && (
                      <button
                        onClick={() => updateStatusMutation.mutate({ id: selectedId, status: 'FINALIZED' })}
                        className="rounded-lg border border-green-300 bg-green-50 px-3 py-1.5 text-xs font-medium text-green-700 hover:bg-green-100"
                      >
                        Finalize
                      </button>
                    )}
                    {detail.overallRiskLevel && (
                      <span className={clsx('inline-flex rounded-full px-2 py-0.5 text-xs font-medium', RISK_LEVEL_BADGE[detail.overallRiskLevel] || RISK_LEVEL_BADGE.LOW)}>
                        {detail.overallRiskLevel} RISK
                      </span>
                    )}
                    <span className={clsx('inline-flex rounded-full px-2 py-0.5 text-xs font-medium', STATUS_BADGE[displayStatus(detail)] || STATUS_BADGE.DRAFT)}>
                      {isFailed(detail) ? 'FAILED' : detail.status}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {detailLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600" />
              </div>
            ) : isFailed(detail) ? (
              <div className="m-4 rounded-md border border-red-200 bg-red-50 p-4">
                <p className="text-sm font-semibold text-red-800">Generation failed</p>
                <p className="mt-1 text-sm text-red-700 whitespace-pre-wrap">{detail.errorMessage}</p>
                <p className="mt-2 text-xs text-red-600">Delete this assessment and generate it again. If it fails again, contact support with the message above.</p>
              </div>
            ) : detailSections && orderedSectionKeys(detailSections).length > 0 ? (
              <div className="divide-y divide-gray-100">
                {orderedSectionKeys(detailSections).map((key) => (
                  <div key={key} className="p-4">
                    <h4 className="text-sm font-semibold text-gray-900">{sectionTitle(key, detailSections[key])}</h4>
                    <div className="mt-2">
                      <SectionValue value={detailSections[key]} />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="py-8 text-center text-sm text-gray-400">
                Assessment content is not yet available. It may still be generating.
              </div>
            )}
          </div>
        )}

        {/* Assessments Table */}
        <div className="rounded-lg border bg-white">
          <div className="border-b px-4 py-3">
            <h3 className="text-sm font-semibold text-gray-900">All Assessments</h3>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600" />
            </div>
          ) : assessments.length === 0 ? (
            <div className="py-12 text-center text-gray-500">
              <ShieldCheckIcon className="mx-auto h-12 w-12 text-gray-300" />
              <p className="mt-4 text-lg font-medium">No risk assessments</p>
              <p className="mt-1 text-sm">Generate your first risk assessment to get started.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Title</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Period</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Risk Level</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Created</th>
                    <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {assessments.map((a: any) => (
                    <tr key={a.id} className="hover:bg-gray-50">
                      <td className="whitespace-nowrap px-4 py-3 text-sm font-medium text-gray-900">{a.title}</td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-600">
                        {formatDate(a.assessmentPeriodStart)} - {formatDate(a.assessmentPeriodEnd)}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3">
                        {a.overallRiskLevel ? (
                          <span className={clsx('inline-flex rounded-full px-2 py-0.5 text-xs font-medium', RISK_LEVEL_BADGE[a.overallRiskLevel] || RISK_LEVEL_BADGE.LOW)}>
                            {a.overallRiskLevel}
                          </span>
                        ) : (
                          <span className="text-sm text-gray-400">--</span>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3">
                        <span
                          className={clsx('inline-flex rounded-full px-2 py-0.5 text-xs font-medium', STATUS_BADGE[displayStatus(a)] || STATUS_BADGE.DRAFT)}
                          title={isFailed(a) ? a.errorMessage : undefined}
                        >
                          {a.status === 'GENERATING' && !isFailed(a) && (
                            <svg className="animate-spin -ml-0.5 mr-1 h-3 w-3" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                            </svg>
                          )}
                          {isFailed(a) ? 'FAILED' : a.status}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-500">
                        {formatDate(a.createdAt)}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => setSelectedId(a.id)}
                            className="inline-flex items-center rounded px-2 py-1 text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100"
                          >
                            <EyeIcon className="mr-1 h-3.5 w-3.5" />
                            View
                          </button>
                          {a.status !== 'GENERATING' && !isFailed(a) && (
                            <button
                              onClick={() => handleDownloadPdf(a.id, a.title)}
                              className="inline-flex items-center rounded px-2 py-1 text-xs font-medium text-gray-700 bg-gray-50 hover:bg-gray-100"
                            >
                              <ArrowDownTrayIcon className="mr-1 h-3.5 w-3.5" />
                              PDF
                            </button>
                          )}
                          <button
                            onClick={() => setDeleteConfirm(a.id)}
                            className="inline-flex items-center rounded px-2 py-1 text-xs font-medium text-red-700 bg-red-50 hover:bg-red-100"
                          >
                            <TrashIcon className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Generate Modal */}
        <Dialog open={showGenerateModal} onClose={() => setShowGenerateModal(false)} className="relative z-50">
          <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
          <div className="fixed inset-0 flex items-center justify-center p-4">
            <Dialog.Panel className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
              <Dialog.Title className="text-lg font-semibold text-gray-900">
                Generate Risk Assessment
              </Dialog.Title>
              <p className="mt-1 text-sm text-gray-500">
                Create a new compliance risk assessment for the specified period.
              </p>

              <div className="mt-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                  <input
                    type="text"
                    value={formTitle}
                    onChange={(e) => setFormTitle(e.target.value)}
                    placeholder="e.g., Q4 2025 Risk Assessment"
                    className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Period Start</label>
                    <input
                      type="date"
                      value={formPeriodStart}
                      onChange={(e) => setFormPeriodStart(e.target.value)}
                      className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Period End</label>
                    <input
                      type="date"
                      value={formPeriodEnd}
                      onChange={(e) => setFormPeriodEnd(e.target.value)}
                      className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>

              <div className="mt-6 flex justify-end gap-3">
                <button
                  onClick={() => { setShowGenerateModal(false); resetForm(); }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900"
                >
                  Cancel
                </button>
                <button
                  onClick={() => generateMutation.mutate()}
                  disabled={!formTitle || !formPeriodStart || !formPeriodEnd || generateMutation.isPending}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-teal-600 rounded-md hover:bg-teal-700 disabled:opacity-50"
                >
                  {generateMutation.isPending ? 'Generating...' : 'Generate'}
                </button>
              </div>
            </Dialog.Panel>
          </div>
        </Dialog>

        {/* Delete Confirmation */}
        <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} className="relative z-50">
          <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
          <div className="fixed inset-0 flex items-center justify-center p-4">
            <Dialog.Panel className="w-full max-w-sm rounded-lg bg-white p-6 shadow-xl">
              <Dialog.Title className="text-lg font-semibold text-gray-900">Delete Assessment</Dialog.Title>
              <p className="mt-2 text-sm text-gray-500">
                Are you sure you want to delete this risk assessment? This action cannot be undone.
              </p>
              <div className="mt-4 flex justify-end gap-3">
                <button
                  onClick={() => setDeleteConfirm(null)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900"
                >
                  Cancel
                </button>
                <button
                  onClick={() => deleteConfirm && deleteMutation.mutate(deleteConfirm)}
                  disabled={deleteMutation.isPending}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50"
                >
                  {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </Dialog.Panel>
          </div>
        </Dialog>
      </div>
    </FeatureGate>
  );
}
